import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faCalendarCheck,
  faCarSide,
  faClock,
  faEuroSign,
  faHandHoldingHeart,
  faHeart,
  faHospital,
  faHouse,
  faPhone,
  faShieldAlt,
  faUserCheck,
  faWheelchair
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import partnerAnima from '../assets/anima-logo.svg';
import partnerCpas from '../assets/cpas-anderlecht-logo.png';
import partnerCocof from '../assets/cocoff.jpg';
import partnerPartenamut from '../assets/partnamut.jpg';
import pricingRates from '../data/pricing.json';
import '../styles/Home.css';

const copy = {
  fr: {
    eyebrow: 'Transport médico-sanitaire agréé à Bruxelles',
    titleStart: 'Vos rendez-vous médicaux,', titleAccent: 'accompagnés', titleEnd: 'avec attention.',
    intro: 'Main Tendue ASBL assure vos transports médicaux non urgents, TPMR et PMR à Bruxelles, en véhicule sanitaire léger ou adapté, de votre porte jusqu’au service de soins.',
    book: 'Réserver un transport', call: 'Nous appeler',
    cocof: 'Agréé COCOF n° 30.01.14', partner: 'Partenaire Partenamut', cpas: 'Partenaire du CPAS d’Anderlecht', anima: 'Partenaire des maisons de repos Anima', availability: '7 jours sur 7, sur rendez-vous',
    destinations: 'Transport vers vos soins', status: 'En route', officialRate: 'Tarif officiel', adapted: 'Véhicule adapté', care: 'Accompagnement jusqu’au service',
    journey: 'Votre trajet', pickup: 'Prise en charge', pickupValue: 'Domicile ou résidence', arrival: 'Arrivée', arrivalValue: 'Jusqu’au service de soins',
    pmrEyebrow: 'Mobilité et confort', pmrTitle: 'Des véhicules adaptés, une présence humaine.',
    pmrText: 'Fauteuil roulant, déambulateur ou besoin d’une aide particulière : nous préparons chaque trajet selon votre situation et restons présents à chaque étape.',
    pmrPoints: ['Rampe d’accès sécurisée', 'Intérieur modulable et climatisé', 'Chauffeurs formés et expérimentés'],
    howEyebrow: 'Simple et rassurant', howTitle: 'Votre transport en trois étapes',
    steps: [
      ['01', 'Vous réservez', 'En ligne ou par téléphone, transmettez-nous le rendez-vous et vos besoins.'],
      ['02', 'Nous préparons', 'Nous confirmons le trajet, le véhicule adapté et l’heure de prise en charge.'],
      ['03', 'Nous vous accompagnons', 'Le chauffeur vient vous chercher et vous guide jusqu’à votre destination.']
    ],
    partnersEyebrow: 'Reconnu et partenaire', partnersTitle: 'Un service encadré, au tarif officiel.',
    partnersText: 'Notre agrément COCOF et nos collaborations avec Partenamut, le CPAS d’Anderlecht et Anima témoignent d’un service fiable, humain et contrôlé.',
    finalEyebrow: 'Un rendez-vous à organiser ?', finalTitle: 'Parlons de votre prochain trajet.',
    finalText: 'Notre équipe vous répond et prépare une prise en charge adaptée à votre situation.'
  },
  nl: {
    eyebrow: 'Erkend niet-dringend medisch vervoer in Brussel',
    titleStart: 'Uw medische afspraken,', titleAccent: 'met zorg', titleEnd: 'begeleid.',
    intro: 'Main Tendue vzw verzorgt uw niet-dringende medische verplaatsingen, van uw deur tot aan de zorgdienst, met opgeleide chauffeurs en aangepaste voertuigen.',
    book: 'Vervoer reserveren', call: 'Bel ons',
    cocof: 'COCOF-erkenning nr. 30.01.14', partner: 'Partner van Partenamut', cpas: 'Partner van het OCMW van Anderlecht', anima: 'Partner van de Anima-woonzorgcentra', availability: '7 dagen per week, op afspraak',
    destinations: 'Vervoer naar uw zorg', status: 'Onderweg', officialRate: 'Officieel tarief', adapted: 'Aangepast voertuig', care: 'Begeleiding tot aan de zorgdienst',
    journey: 'Uw traject', pickup: 'Ophalen', pickupValue: 'Thuis of in de residentie', arrival: 'Aankomst', arrivalValue: 'Tot aan de zorgdienst',
    pmrEyebrow: 'Mobiliteit en comfort', pmrTitle: 'Aangepaste voertuigen, menselijke begeleiding.',
    pmrText: 'Rolstoel, rollator of specifieke hulp nodig? Wij bereiden elke rit volgens uw situatie voor en blijven aanwezig bij elke stap.',
    pmrPoints: ['Veilige toegangshelling', 'Modulair interieur met airconditioning', 'Opgeleide en ervaren chauffeurs'],
    howEyebrow: 'Eenvoudig en geruststellend', howTitle: 'Uw vervoer in drie stappen',
    steps: [
      ['01', 'U reserveert', 'Online of telefonisch geeft u de afspraak en uw behoeften door.'],
      ['02', 'Wij bereiden voor', 'Wij bevestigen het traject, het aangepaste voertuig en het ophaaluur.'],
      ['03', 'Wij begeleiden u', 'De chauffeur haalt u op en begeleidt u tot aan uw bestemming.']
    ],
    partnersEyebrow: 'Erkend en vertrouwd', partnersTitle: 'Een gereglementeerde dienst aan het officiële tarief.',
    partnersText: 'Onze COCOF-erkenning en samenwerkingen met Partenamut, het OCMW van Anderlecht en Anima staan voor een betrouwbare, menselijke en gecontroleerde dienstverlening.',
    finalEyebrow: 'Een afspraak te organiseren?', finalTitle: 'Vertel ons over uw volgende rit.',
    finalText: 'Ons team beantwoordt uw vragen en bereidt vervoer op maat van uw situatie voor.'
  }
};

const reveal = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0 } };

function Home() {
  const { i18n } = useTranslation();
  const reducedMotion = useReducedMotion();
  const processRef = React.useRef(null);
  const processInView = useInView(processRef, { once: true, amount: 0.25 });
  const lang = i18n.resolvedLanguage?.startsWith('nl') ? 'nl' : 'fr';
  const c = copy[lang];
  const baseRate = new Intl.NumberFormat(lang === 'fr' ? 'fr-BE' : 'nl-BE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2
  }).format(pricingRates.baseRate);
  const motionProps = reducedMotion ? {} : { initial: 'hidden', whileInView: 'visible', viewport: { once: true, amount: 0.2 }, variants: reveal };

  return (
    <div className="modern-home">
      <section className="mt-hero" aria-labelledby="home-title">
        <div className="mt-shell mt-hero-inner">
          <motion.div className="mt-hero-copy" initial={reducedMotion ? false : { opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65 }}>
            <p className="mt-eyebrow"><span></span>{c.eyebrow}</p>
            <h1 id="home-title">
              {c.titleStart}{' '}
              <span className="mt-title-accent">{c.titleAccent}</span>{' '}
              {c.titleEnd}
            </h1>
            <p className="mt-hero-intro">{c.intro}</p>
            <div className="mt-hero-actions">
              <Link className="mt-button mt-button-primary" to="/reservation">{c.book}<FontAwesomeIcon icon={faArrowRight} /></Link>
              <a className="mt-button mt-button-secondary" href="tel:+32491333777"><FontAwesomeIcon icon={faPhone} />{c.call}</a>
            </div>
          </motion.div>

          <motion.div
            className="mt-hero-visual"
            initial={reducedMotion ? false : { opacity: 0, x: 42, rotate: 2 }}
            animate={{ opacity: 1, x: 0, rotate: 0 }}
            transition={{ duration: 0.8, delay: 0.12, ease: 'easeOut' }}
            aria-hidden="true"
          >
            <motion.div
              className="mt-hero-orbit"
              animate={reducedMotion ? undefined : { rotate: [0, 360] }}
              transition={{ duration: 36, repeat: Infinity, ease: 'linear' }}
            >
              <span></span><span></span><span></span>
            </motion.div>
            <motion.div
              className="mt-hero-route-card"
              animate={reducedMotion ? undefined : { y: [0, -8, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
            >
              <div className="mt-hero-card-top">
                <div><small>{c.journey}</small><strong>{c.destinations}</strong></div>
                <span><i></i>{c.status}</span>
              </div>
              <div className="mt-hero-map">
                <i className="mt-map-road road-one"></i>
                <i className="mt-map-road road-two"></i>
                <i className="mt-map-road road-three"></i>
                <span className="mt-map-block block-one"></span>
                <span className="mt-map-block block-two"></span>
                <span className="mt-map-block block-three"></span>
                <span className="mt-map-block block-four"></span>
                <span className="mt-map-park park-one"></span>
                <span className="mt-map-park park-two"></span>
                <span className="mt-hero-route-track track-one"></span>
                <span className="mt-hero-route-track track-two"></span>
                <span className="mt-hero-route-track track-three"></span>
                <span className="mt-hero-route-node start"><FontAwesomeIcon icon={faHouse} /></span>
                <motion.span
                  className="mt-hero-route-vehicle"
                  animate={reducedMotion ? undefined : { x: [0, 66, 134, 196], y: [0, -8, -30, -62] }}
                  transition={{ duration: 4.8, repeat: Infinity, repeatDelay: 1, ease: 'easeInOut' }}
                ><FontAwesomeIcon icon={faCarSide} /></motion.span>
                <span className="mt-hero-route-node end"><FontAwesomeIcon icon={faHospital} /></span>
              </div>
              <div className="mt-hero-card-details">
                <span><FontAwesomeIcon icon={faHouse} /><small>{c.pickup}</small><strong>{c.pickupValue}</strong></span>
                <span><FontAwesomeIcon icon={faHospital} /><small>{c.arrival}</small><strong>{c.arrivalValue}</strong></span>
              </div>
              <div className="mt-hero-care-line"><FontAwesomeIcon icon={faHandHoldingHeart} />{c.care}</div>
            </motion.div>
            <motion.div className="mt-hero-float-badge badge-cocof" animate={reducedMotion ? undefined : { y: [0, -5, 0] }} transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}>
              <FontAwesomeIcon icon={faShieldAlt} /><span><strong>{lang === 'fr' ? 'Agréé COCOF' : 'COCOF-erkend'}</strong><small>n° 30.01.14</small></span>
            </motion.div>
            <motion.div className="mt-hero-float-badge badge-price" animate={reducedMotion ? undefined : { y: [0, 5, 0] }} transition={{ duration: 4.8, repeat: Infinity, ease: 'easeInOut' }}>
              <FontAwesomeIcon icon={faEuroSign} /><span><strong>{baseRate}</strong><small>{c.officialRate}</small></span>
            </motion.div>
            <motion.div className="mt-hero-float-badge badge-pmr" animate={reducedMotion ? undefined : { x: [0, 4, 0] }} transition={{ duration: 5.2, repeat: Infinity, ease: 'easeInOut' }}>
              <FontAwesomeIcon icon={faWheelchair} /><span><strong>PMR</strong><small>{c.adapted}</small></span>
            </motion.div>
          </motion.div>
        </div>
        <div className="mt-trust-strip">
          <div className="mt-shell mt-trust-grid">
            <span><FontAwesomeIcon icon={faShieldAlt} />{c.cocof}</span>
            <span><FontAwesomeIcon icon={faHeart} />{c.partner}</span>
            <span><FontAwesomeIcon icon={faCalendarCheck} />{c.availability}</span>
          </div>
        </div>
      </section>

      <section className="mt-section mt-accessibility-section">
        <div className="mt-shell mt-accessibility-grid">
          <motion.div className="mt-copy-column mt-accessibility-heading" {...motionProps} transition={{ duration: 0.55 }}>
            <p className="mt-section-label mt-section-label-warm"><FontAwesomeIcon icon={faWheelchair} />{c.pmrEyebrow}</p>
            <h2>{c.pmrTitle}</h2>
          </motion.div>
          <motion.div className="mt-copy-column mt-accessibility-details" {...motionProps} transition={{ duration: 0.55, delay: 0.1 }}>
            <p className="mt-lead">{c.pmrText}</p>
            <div className="mt-feature-lines">{c.pmrPoints.map((item, index) => <div key={item}><span>0{index + 1}</span><strong>{item}</strong></div>)}</div>
            <Link className="mt-button mt-button-dark" to="/reservation">{c.book}<FontAwesomeIcon icon={faArrowRight} /></Link>
          </motion.div>
        </div>
      </section>

      <section className="mt-section mt-process-section">
        <div className="mt-shell">
          <motion.div className="mt-section-heading" {...motionProps}>
            <p className="mt-section-label"><FontAwesomeIcon icon={faClock} />{c.howEyebrow}</p>
            <h2>{c.howTitle}</h2>
          </motion.div>
          <motion.div
            className="mt-process-flow"
            ref={processRef}
            initial={reducedMotion ? false : 'hidden'}
            whileInView="visible"
            viewport={{ once: true, amount: 0.28 }}
            variants={{ visible: { transition: { staggerChildren: 0.14, delayChildren: 0.12 } } }}
          >
            <svg className="mt-process-route mt-process-route-desktop" viewBox="0 0 1180 650" preserveAspectRatio="none" aria-hidden="true">
              <defs>
                <linearGradient id="processRouteGradient" x1="0" x2="1">
                  <stop offset="0" stopColor="#f3a33b" />
                  <stop offset="0.52" stopColor="#168992" />
                  <stop offset="1" stopColor="#1b4b72" />
                </linearGradient>
                <filter id="processSignalGlow" x="-250%" y="-250%" width="600%" height="600%">
                  <feGaussianBlur stdDeviation="4" result="blur" />
                  <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
              </defs>
              <path className="mt-process-route-base" d="M330 238 H365 Q380 238 380 226 V222 Q380 210 395 210 H425" />
              <path className="mt-process-route-base" d="M755 408 H790 Q805 408 805 396 V392 Q805 380 820 380 H850" />
              <motion.path
                className="mt-process-route-active"
                d="M330 238 H365 Q380 238 380 226 V222 Q380 210 395 210 H425"
                initial={reducedMotion ? false : { pathLength: 0, opacity: 0 }}
                animate={processInView ? { pathLength: 1, opacity: 1 } : undefined}
                transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              />
              <motion.path
                className="mt-process-route-active"
                d="M755 408 H790 Q805 408 805 396 V392 Q805 380 820 380 H850"
                initial={reducedMotion ? false : { pathLength: 0, opacity: 0 }}
                animate={processInView ? { pathLength: 1, opacity: 1 } : undefined}
                transition={{ duration: 0.8, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
              />
              {processInView && !reducedMotion && (
                <>
                  <circle className="mt-process-signal" r="6" cx="0" cy="0">
                    <animateMotion dur="8s" repeatCount="indefinite" path="M330 238 H365 Q380 238 380 226 V222 Q380 210 395 210 H425" keyPoints="0;0;1;1" keyTimes="0;0.06;0.28;1" calcMode="linear" />
                    <animate attributeName="opacity" values="0;1;1;0;0" keyTimes="0;0.06;0.26;0.28;1" dur="8s" repeatCount="indefinite" />
                  </circle>
                  <circle className="mt-process-signal" r="6" cx="0" cy="0">
                    <animateMotion dur="8s" repeatCount="indefinite" path="M755 408 H790 Q805 408 805 396 V392 Q805 380 820 380 H850" keyPoints="0;0;1;1" keyTimes="0;0.38;0.62;1" calcMode="linear" />
                    <animate attributeName="opacity" values="0;0;1;1;0;0" keyTimes="0;0.36;0.38;0.60;0.62;1" dur="8s" repeatCount="indefinite" />
                  </circle>
                </>
              )}
            </svg>
            <svg className="mt-process-route mt-process-route-mobile" viewBox="0 0 40 1000" preserveAspectRatio="none" aria-hidden="true">
              <path className="mt-process-route-base" d="M20 150 V850" />
              <motion.path
                className="mt-process-route-active"
                d="M20 150 V850"
                initial={reducedMotion ? false : { pathLength: 0, opacity: 0 }}
                animate={processInView ? { pathLength: 1, opacity: 1 } : undefined}
                transition={{ duration: 1.2, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              />
              {processInView && !reducedMotion && (
                <circle className="mt-process-signal" r="7" cx="0" cy="0">
                  <animateMotion dur="8s" repeatCount="indefinite" path="M20 150 V850" keyPoints="0;0;0.5;1;1" keyTimes="0;0.04;0.28;0.62;1" calcMode="linear" />
                  <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.04;0.62;1" dur="8s" repeatCount="indefinite" />
                </circle>
              )}
            </svg>
            {c.steps.map(([number, title, text], index) => (
              <motion.article
                key={number}
                className={`mt-process-step mt-process-step-${index + 1}`}
                variants={reducedMotion ? undefined : {
                  hidden: { opacity: 0, y: 24 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
                }}
              >
                <motion.div
                  className="mt-process-card-surface"
                  animate={processInView && !reducedMotion ? {
                    y: [0, 0, -7, 0, 0],
                    scale: [1, 1, 1.015, 1, 1]
                  } : undefined}
                  transition={{
                    duration: 8,
                    times: [0, [0.01, 0.245, 0.585][index], [0.04, 0.28, 0.62][index], [0.09, 0.33, 0.67][index], 1],
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }}
                >
                  <div className="mt-step-marker">
                    <span className="mt-step-number">{number}</span>
                    <span className="mt-step-icon"><FontAwesomeIcon icon={[faCalendarCheck, faUserCheck, faHeart][index]} /></span>
                  </div>
                  <div className="mt-step-copy"><h3>{title}</h3><p>{text}</p></div>
                  <motion.span
                    className="mt-route-stop"
                    aria-hidden="true"
                    animate={processInView && !reducedMotion ? {
                      scale: [1, 1, 1.55, 1, 1],
                      boxShadow: [
                        '0 0 0 5px rgba(255,255,255,.95)',
                        '0 0 0 5px rgba(255,255,255,.95)',
                        '0 0 0 10px rgba(243,163,59,.2)',
                        '0 0 0 5px rgba(255,255,255,.95)',
                        '0 0 0 5px rgba(255,255,255,.95)'
                      ]
                    } : undefined}
                    transition={{
                      duration: 8,
                      times: [0, [0.01, 0.245, 0.585][index], [0.04, 0.28, 0.62][index], [0.09, 0.33, 0.67][index], 1],
                      repeat: Infinity,
                      ease: 'easeInOut'
                    }}
                  />
                  {index < c.steps.length - 1 && <span className="mt-route-exit" aria-hidden="true" />}
                </motion.div>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="mt-section mt-partners-section">
        <div className="mt-shell mt-partners-layout">
          <motion.div className="mt-copy-column" {...motionProps}>
            <p className="mt-section-label mt-section-label-warm"><FontAwesomeIcon icon={faShieldAlt} />{c.partnersEyebrow}</p>
            <h2>{c.partnersTitle}</h2><p className="mt-lead">{c.partnersText}</p>
          </motion.div>
          <motion.div className="mt-partner-logos" {...motionProps} transition={{ duration: 0.55, delay: 0.1 }}>
            <a href="https://ccf.brussels/nos-services/bien-etre-et-sante/accueil-aide-et-soins/transport-medico-sanitaire/" target="_blank" rel="noreferrer">
              <span className="mt-partner-logo"><img src={partnerCocof} alt="COCOF" /></span>
              <span>{c.cocof}</span>
            </a>
            <a href="https://www.partenamut.be/fr/services-ma-mutuelle/transport-medical-non-urgent" target="_blank" rel="noreferrer">
              <span className="mt-partner-logo"><img src={partnerPartenamut} alt="Partenamut" /></span>
              <span>{c.partner}</span>
            </a>
            <a href="https://cpas-ocmw.anderlecht.be/" target="_blank" rel="noreferrer">
              <span className="mt-partner-logo mt-partner-logo--cpas"><img src={partnerCpas} alt="CPAS OCMW Anderlecht" /></span>
              <span>{c.cpas}</span>
            </a>
            <a href="https://animagroup.be/fr-be/" target="_blank" rel="noreferrer">
              <span className="mt-partner-logo mt-partner-logo--anima"><img src={partnerAnima} alt="Anima" /></span>
              <span>{c.anima}</span>
            </a>
          </motion.div>
        </div>
      </section>

      <section className="mt-final-cta">
        <div className="mt-shell mt-final-cta-inner">
          <div><p>{c.finalEyebrow}</p><h2>{c.finalTitle}</h2><span>{c.finalText}</span></div>
          <div className="mt-final-actions">
            <Link className="mt-button mt-button-primary" to="/reservation">{c.book}<FontAwesomeIcon icon={faArrowRight} /></Link>
            <a className="mt-button mt-button-light" href="tel:+32491333777"><FontAwesomeIcon icon={faPhone} />+32 491 33 37 77</a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
